import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

/**
 * Survey Response Filter Stimulus Controller
 * 
 * Handles dynamic loading of survey questions and response options
 */
export default class extends Controller {
  static targets = [
    "surveySelect", 
    "questionContainer", 
    "questionSelect", 
    "responseContainer", 
    "responseChoices",
    "textInputContainer",
    "textInput",
    "checkboxContainer"
  ]

  static values = {
    selectedResponseValues: String,
    searchText: String
  }

  declare readonly surveySelectTarget: HTMLSelectElement
  declare readonly questionContainerTarget: HTMLElement
  declare readonly questionSelectTarget: HTMLSelectElement
  declare readonly responseContainerTarget: HTMLElement
  declare readonly responseChoicesTarget: HTMLElement
  declare readonly textInputContainerTarget: HTMLElement
  declare readonly textInputTarget: HTMLInputElement
  declare readonly checkboxContainerTarget: HTMLElement

  declare readonly selectedResponseValuesValue: string
  declare readonly searchTextValue: string
  private currentQuestionType: string | null = null

  connect(): void {
    // Set up form submission cleanup
    this.setupFormCleanup()
    
    // Initialize with existing values if any
    this.initializeFromFormValues()
  }

  private setupFormCleanup(): void {
    const form = this.element.closest('form') as HTMLFormElement
    if (form) {
      form.addEventListener('submit', () => {
        // Remove conflicting empty survey_responses field before serialization
        const emptyField = form.querySelector('input[name*="[survey_responses]"][value=""]') as HTMLInputElement
        if (emptyField && emptyField.name.endsWith('[survey_responses]')) {
          emptyField.remove()
        }
      })
    }
  }

  surveyChanged(): void {
    const surveyId = this.surveySelectTarget.value
    if (surveyId) {
      this.loadQuestions(surveyId)
      this.showQuestionContainer()
    } else {
      this.hideQuestionAndResponseContainers()
    }
  }

  questionChanged(): void {
    const questionId = this.questionSelectTarget.value
    if (questionId) {
      this.loadQuestionAndShowInput(questionId)
    } else {
      this.hideResponseContainer()
    }
  }

  private async initializeFromFormValues(): Promise<void> {
    // Check if survey is already selected
    const surveyId = this.surveySelectTarget.value
    if (surveyId) {
      // Check if questions are already populated server-side (more than just the default option)
      const existingOptions = this.questionSelectTarget.options.length
      
      if (existingOptions <= 1) {
        // Questions not populated server-side, load them dynamically
        await this.loadQuestions(surveyId)
      }
      
      this.showQuestionContainer()

      // Check if question is already selected (from server-side rendering)
      const questionId = this.questionSelectTarget.value
      
      if (questionId) {
        await this.loadQuestionAndShowInput(questionId)
        
        // Restore selected response values after loading the input
        await this.restoreSelectedValues()
      }
    }
  }

  private async restoreSelectedValues(): Promise<void> {
    const selectedResponseValues = this.selectedResponseValuesValue
    const searchTextValue = this.searchTextValue
    
    if (this.isMultipleChoiceQuestion() && selectedResponseValues) {
      // Wait for checkboxes to be created, then select them
      const selectedValues = selectedResponseValues.split(',').filter(v => v.trim())
      
      if (selectedValues.length > 0) {
        // Use a promise-based approach with timeout
        await this.waitForCheckboxes()
        this.selectResponseValues(selectedValues)
      }
    } else if (!this.isMultipleChoiceQuestion() && searchTextValue && this.hasTextInputTarget) {
      // Restore text input value
      this.textInputTarget.value = searchTextValue
    }
  }

  private waitForCheckboxes(): Promise<void> {
    return new Promise((resolve) => {
      const maxAttempts = 20 // Maximum 1 second (20 * 50ms)
      let attempts = 0
      
      const checkForCheckboxes = () => {
        const checkboxes = this.responseChoicesTarget.querySelectorAll('input[type="checkbox"]')
        
        if (checkboxes.length > 0) {
          resolve()
        } else if (attempts < maxAttempts) {
          attempts++
          setTimeout(checkForCheckboxes, 50)
        } else {
          // Timeout reached, resolve anyway to prevent hanging
          console.warn('Survey response filter: Timeout waiting for checkboxes to be created')
          resolve()
        }
      }
      
      checkForCheckboxes()
    })
  }

  private async loadQuestions(surveyId: string): Promise<void> {
    try {
      const response = await fetch(`/surveys/${surveyId}/questions`)
      
      if (!response.ok) {
        throw new Error(`Failed to load questions: ${response.status} ${response.statusText}`)
      }
      
      const questions = await response.json()
      
      // Get the Tom Select instance
      const tomSelectInstance = (this.questionSelectTarget as any).tomselect
      if (tomSelectInstance) {
        // Clear existing options
        tomSelectInstance.clearOptions()
        
        // Add new options
        questions.forEach((q: any) => {
          tomSelectInstance.addOption({
            value: q.id,
            text: q.text,
            'data-question-type': q.question_type
          })
        })
        
        // Refresh the dropdown
        tomSelectInstance.refreshOptions()
      } else {
        // Fallback to direct DOM manipulation if Tom Select isn't initialized
        this.questionSelectTarget.innerHTML = `<option value="">${i18n.t('survey_response_filter.select_a_question')}</option>`
        questions.forEach((q: any) => {
          const option = document.createElement('option')
          option.value = q.id
          option.textContent = q.text
          option.dataset.questionType = q.question_type
          this.questionSelectTarget.appendChild(option)
        })
      }
    } catch (error) {
      console.error('Error loading questions:', error)
      this.showErrorMessage(i18n.t('survey_response_filter_messages.failed_to_load_questions'))
    }
  }

  private async loadQuestionAndShowInput(questionId: string): Promise<void> {
    // Ensure the question is selected in the dropdown if not already
    if (this.questionSelectTarget.value !== questionId) {
      this.questionSelectTarget.value = questionId
    }
    
    // Get question type from the selected option
    const selectedOption = this.questionSelectTarget.selectedOptions[0]
    this.currentQuestionType = selectedOption?.dataset.questionType || null

    this.showResponseContainer()

    if (this.isMultipleChoiceQuestion()) {
      await this.loadResponseChoices(questionId)
      this.showCheckboxContainer()
      this.hideTextInputContainer()
    } else {
      this.showTextInputContainer()
      this.hideCheckboxContainer()
    }
  }

  private isMultipleChoiceQuestion(): boolean {
    return this.currentQuestionType === 'multiple_choice' || 
           this.currentQuestionType === 'checkbox' ||
           this.currentQuestionType === 'radio'
  }

  private async loadResponseChoices(questionId: string): Promise<void> {
    try {
      const response = await fetch(`/survey_questions/${questionId}/response_options`)
      
      if (!response.ok) {
        throw new Error(`Failed to load response options: ${response.status} ${response.statusText}`)
      }
      
      const options = await response.json()
      
      // Clear and populate with checkboxes
      this.responseChoicesTarget.innerHTML = ''
      options.forEach((option: any) => {
        const checkbox = this.createResponseCheckbox(option)
        this.responseChoicesTarget.appendChild(checkbox)
      })
      
    } catch (error) {
      console.error('Error loading response options:', error)
      this.showErrorMessage(i18n.t('survey_response_filter_messages.failed_to_load_options'))
    }
  }



  private createResponseCheckbox(option: any): HTMLElement {
    const container = document.createElement('div')
    container.className = 'form-control'
    
    const label = document.createElement('label')
    label.className = 'label cursor-pointer justify-start gap-2'
    
    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    checkbox.className = 'checkbox checkbox-sm'
    
    try {
      const formObjectName = this.getFormObjectName()
      checkbox.name = `${formObjectName}[response_values][]`
      checkbox.value = option.value
    } catch (error) {
      console.error('Error creating checkbox:', error)
      return container
    }
    
    const labelText = document.createElement('span')
    labelText.className = 'label-text'
    labelText.textContent = option.label || option.value
    
    label.appendChild(checkbox)
    label.appendChild(labelText)
    container.appendChild(label)
    
    return container
  }

  private selectResponseValues(selectedValues: string[]): void {
    if (selectedValues.length > 0) {
      // Set selected values on checkboxes
      const checkboxes = this.responseChoicesTarget.querySelectorAll('input[type="checkbox"]')
      checkboxes.forEach((checkbox: Element) => {
        const input = checkbox as HTMLInputElement
        input.checked = selectedValues.includes(input.value)
      })
    }
  }

  private getFormObjectName(): string {
    // Extract the full path from the survey select field
    // Survey select name looks like: metric[filter_config][survey_responses][survey_id]
    // We want to generate: metric[filter_config][survey_responses]
    const surveySelectName = this.surveySelectTarget.name
    const fullPathMatch = surveySelectName.match(/^(.+)\[survey_id\]$/)
    
    if (fullPathMatch) {
      return fullPathMatch[1]
    }
    
    throw new Error('Survey response filter: unable to determine form object name')
  }

  private showQuestionContainer(): void {
    this.questionContainerTarget.style.display = 'block'
  }

  private showResponseContainer(): void {
    this.responseContainerTarget.style.display = 'block'
  }

  private showTextInputContainer(): void {
    this.textInputContainerTarget.style.display = 'block'
  }

  private hideTextInputContainer(): void {
    this.textInputContainerTarget.style.display = 'none'
  }

  private showCheckboxContainer(): void {
    this.checkboxContainerTarget.style.display = 'block'
  }

  private hideCheckboxContainer(): void {
    this.checkboxContainerTarget.style.display = 'none'
  }

  private hideQuestionAndResponseContainers(): void {
    this.questionContainerTarget.style.display = 'none'
    this.hideResponseContainer()
    this.clearQuestionSelect()
  }

  private hideResponseContainer(): void {
    this.responseContainerTarget.style.display = 'none'
    this.hideTextInputContainer()
    this.hideCheckboxContainer()
    this.clearResponseInputs()
  }

  private clearQuestionSelect(): void {
    this.questionSelectTarget.innerHTML = `<option value="">${i18n.t('survey_response_filter.select_a_question')}</option>`
  }

  private clearResponseInputs(): void {
    // Clear dynamically generated checkboxes
    this.responseChoicesTarget.innerHTML = ''
    
    // Clear text input value but preserve the element
    if (this.hasTextInputTarget) {
      this.textInputTarget.value = ''
    }
  }

  private showErrorMessage(message: string): void {
    // Create a simple error message that disappears after a few seconds
    const errorDiv = document.createElement('div')
    errorDiv.className = 'alert alert-error mt-2'
    errorDiv.textContent = message
    
    // Insert after the survey select
    this.element.insertBefore(errorDiv, this.questionContainerTarget)
    
    // Remove after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv)
      }
    }, 5000)
  }
}